"use client";

import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { useCallback } from "react";
import {
  Squares2X2Icon,
  Bars3Icon,
  ClockIcon,
} from "@heroicons/react/24/outline";

type CategoryToolbarProps = {
  totalResults: number;
  categoryName: string;
};

export default function CategoryToolbar({
  totalResults,
  categoryName,
}: CategoryToolbarProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const sortBy = searchParams.get("sort") || "relevance";
  const viewMode = (searchParams.get("view") as "grid" | "list") || "grid";
  const isOpenNow = searchParams.get("open_now") === "true";

  const updateParam = useCallback(
    (key: string, value: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set(key, value);
      router.push(`${pathname}?${params.toString()}`);
    },
    [pathname, router, searchParams]
  );

  const toggleOpenNow = () => {
    const params = new URLSearchParams(searchParams.toString());
    if (isOpenNow) {
      params.delete("open_now");
    } else {
      params.set("open_now", "true");
    }
    params.delete("page");
    router.push(`${pathname}?${params.toString()}`);
  };

  return (
    <div className="bg-white border-b border-gray-200 sticky top-16 z-10">
      <div className="px-4 py-4 flex items-center justify-between gap-4">
        {/* Resultados */}
        <div className="flex items-center gap-2">
          <h2 className="font-semibold text-gray-900">{categoryName}</h2>
          <span className="text-sm text-gray-500">
            ({totalResults} {totalResults === 1 ? "resultado" : "resultados"})
          </span>
        </div>

        {/* Controles */}
        <div className="flex items-center gap-4">
          {/* Filtro Aberto Agora */}
          <button
            onClick={toggleOpenNow}
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors flex gap-2 ${
              isOpenNow
                ? "bg-mygreen text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            <ClockIcon className="w-4 h-4" />
            <span>Aberto Agora</span>
          </button>

          {/* Ordenação */}
          <div className="flex items-center gap-2">
            <label
              htmlFor="sort"
              className="text-sm text-gray-600 hidden sm:block"
            >
              Ordenar por:
            </label>
            <select
              id="sort"
              value={sortBy}
              onChange={(e) => updateParam("sort", e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-mygreen focus:border-transparent bg-white"
            >
              <option value="relevance">Relevância</option>
              <option value="name-asc">Nome (A-Z)</option>
              <option value="name-desc">Nome (Z-A)</option>
              <option value="rating">Melhor Avaliadas</option>
              <option value="newest">Mais Recentes</option>
            </select>
          </div>

          {/* Modo de Visualização - Desktop */}
          <div className="hidden md:flex items-center gap-1 border border-gray-300 rounded-lg p-1">
            <button
              onClick={() => updateParam("view", "grid")}
              className={`p-1.5 rounded transition-colors ${
                viewMode === "grid"
                  ? "bg-mygreen text-white"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
              title="Visualização em grade"
            >
              <Squares2X2Icon className="w-5 h-5" />
            </button>
            <button
              onClick={() => updateParam("view", "list")}
              className={`p-1.5 rounded transition-colors ${
                viewMode === "list"
                  ? "bg-mygreen text-white"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
              title="Visualização em lista"
            >
              <Bars3Icon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
